import React from 'react';

import { Box } from '@/components/shared/layout/box';
import { Image } from '@/components/shared/media/image';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { Pattern } from '@/components/shared/my24/pattern';
import { styled } from '@/stitches.config';
import { PickupModelType } from '@/types/shared/my24/pickup-model';

const PatternContainer = styled(Box, {
  variants: {
    show: {
      false: {
        transform: 'scale(1.1,1) translate(30%,0)',
        '@lg': {
          transform: 'scale(1.1,1) translate(-30%,0)',
        },
      },
      true: {
        transitionProperty: 'transform',
        transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        transitionDuration: '0.9s',
        transform: 'scale(1,1) translate(0,0)',
        '@lg': {
          transform: 'scale(1,1) translate(0,0)',
        },
      },
    },
  },
});

const HeroContentPatternSvg = styled(Box, {
  position: 'absolute',
  top: 0,
  right: 0,
  width: 'calc(72 / 360 * 100%)',
  height: 'auto',
  aspectRatio: '72 / 240',
  transform: 'skewX(16.7deg)',
  transformOrigin: 'top',
  overflow: 'hidden',
  '-webkit-mask-image':
    'linear-gradient(white 0%, white 20%, transparent 80%, transparent 100%)',
  '@lg': {
    transform: 'skewX(-16.4deg)',
    width: 'calc(338 / 1440 * 100%)',
    aspectRatio: '338 / 700',
    height: '100%',
    right: 'auto',
    left: 0,
    '-webkit-mask-image':
      'linear-gradient(white 0%, white 30%, transparent 80%, transparent 100%)',
  },
  '& svg': {
    transform: 'skewX(-16.7deg)',
    transformOrigin: 'top',
    '@lg': {
      transform: 'skewX(16.4deg)',
    },
  },
  variants: {
    model: {
      'v-cross': {
        opacity: 0.95,
        '& svg': {
          opacity: 0.6,
        },
      },
      '4-door': {
        opacity: 0.8,
        '@lg': {
          opacity: 0.9,
        },
        '& svg': {
          opacity: 1,
        },
      },
      '2-door': {
        opacity: 0.9,
        '& svg': {
          opacity: 1,
        },
      },
      spark: {
        opacity: 0.9,
        '& svg': {
          opacity: 1,
        },
      },
    },
  },
});

const HeroContentPattern1 = styled(Box, {
  transform: 'skewX(-16.7deg)',
  transformOrigin: 'top',
  '@lg': {
    transform: 'skewX(16.4deg)',
  },
  variants: {
    model: {
      'v-cross': {
        bc: '#DC0000',
        opacity: 0.9,
        mixBlendMode: 'multiply',
      },
      '4-door': {
        bc: '$white',
        opacity: 0.5,
      },
      '2-door': {
        bc: '#454545',
        opacity: 0.7,
      },
      spark: {
        bc: '$white',
        opacity: 0.5,
      },
    },
  },
});

const HeroContentPattern2 = styled(Box, {
  width: 'calc(72 / 360 * 100%)',
  height: 'auto',
  right: 0,
  top: 0,
  aspectRatio: '72 / 240',
  transform: 'skewX(16.7deg)',
  transformOrigin: 'top',
  bc: '$white',
  mixBlendMode: 'overlay',
  opacity: 0.3,
  '-webkit-mask-image':
    'linear-gradient(white 0%, white 10%, transparent 80%, transparent 100%)',
  '@lg': {
    transform: 'skewX(-16.4deg)',
    width: 'calc(338 / 1440 * 100%)',
    aspectRatio: '338 / 700',
    height: '100%',
    right: 'auto',
    left: 0,
    '-webkit-mask-image':
      'linear-gradient(white 0%, white 30%, transparent 80%, transparent 100%)',
  },
  variants: {
    model: {
      'v-cross': {
        bc: '#DC0000',
        opacity: 0.4,
      },
      '4-door': {},
      '2-door': {},
      spark: {},
    },
  },
});

const BannerLogo = styled(Box, {
  width: '100%',
  '@media (orientation: portrait)': {
    top: `${(40 / 640) * 100 + '%'}`,
  },
  '@media (orientation: landscape)': {
    top: `${(40 / 640) * 100 + '%'}`,
  },
  '@lg': {
    width: 'calc(294 / 1440  * 100%)',
    top: `${(40 / 800) * 100 + '%'}`,
  },
  variants: {
    show: {
      false: {
        transform: 'translate(0,30px)',
        opacity: 0,
      },
      true: {
        transform: 'translate(0,0)',
        opaicty: 1,
        transitionProperty: 'transform, opacity',
        transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
        transitionDelay: '0.4s',
        transitionDuration: '0.8s',
      },
    },
  },
});

const BannerLogoBox = styled(Box, {
  position: 'relative',
  width: 'clamp(160px, calc(160 / 360  * 100%), 214px)',
  aspectRatio: '214 / 151',
  mx: 'auto',
  '@lg': {
    width: 'calc(214 / 294  * 100%)',
  },
});

interface MiuraLogoProps
  extends React.ComponentProps<typeof HeroContentPatternSvg> {
  model?: PickupModelType;
  logo?: ImageProps;
  logoDesktop?: ImageProps;
  show?: boolean;
}

export const MiuraLogo: React.FC<MiuraLogoProps> = ({
  model = 'v-cross',
  logo,
  logoDesktop,
  show,
}) => {
  return (
    <PatternContainer
      show={show}
      position="absolute"
      inset="0"
      pointerEvents="none"
    >
      <HeroContentPatternSvg model={model}>
        <Pattern />
        <HeroContentPattern1
          position="absolute"
          left="0"
          top="0"
          width="full"
          height="full"
          model={model}
        />
      </HeroContentPatternSvg>
      <HeroContentPattern2
        position="absolute"
        overflow="hidden"
        model={model}
      />
      {!!logo?.src && (
        <BannerLogo position="absolute" left="0" zIndex="1" show={show}>
          <BannerLogoBox
            css={{
              '--banner-logo-with-miura-m-width': logo?.width,
              '--banner-logo-with-miura-m-height': logo?.height,
              '--banner-logo-with-miura-w-width': logoDesktop?.width,
              '--banner-logo-with-miura-w-height': logoDesktop?.height,
            }}
          >
            <Image
              src={logo?.src}
              alt={logo?.alt}
              width={logo?.width}
              height={logo?.height}
              layout="fill"
              objectFit="contain"
              sources={[
                {
                  srcset: logoDesktop?.src as string,
                  media: { minWidth: 1024 },
                  options: {
                    width: 320,
                  },
                },
                {
                  srcset: logo?.src,
                  media: { maxWidth: 1023 },
                  options: {
                    width: 214,
                  },
                },
              ]}
            />
          </BannerLogoBox>
        </BannerLogo>
      )}
    </PatternContainer>
  );
};

MiuraLogo.displayName = 'SharedMY24MiuraLogo';
